import classNames from "classnames";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";

import Head from "pages/_components/Head";
import I18n from "pages/_components/I18n";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
// import Button from "pages/_components/Button";
import { Button } from "@ui-kit/components/index.es";
import ContextMenu from "pages/_components/ContextMenu";

import {
    actions as multipleTransferActions,
    selectors as multipleTransferSelectors,
} from "reducers/multiple-transfers";
import { actions as notificationActions } from "reducers/notification";
import { actions as globalActions } from "reducers/types/global";

import { resizableRoute } from "pages/_components/Resizable";
import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";
import MultipleTransferFilters from "./MultipleTransferFilters";
import MultipleTransferList from "./MultipleTransferList";

function MultipleTransferDashboard({
    isDesktop,
    isSmallDesktop,
    dispatch,
    fetching,
    setValues,
    pageNumber,
    totalPages,
    filters,
    statusList,
    history,
    location,
    embeddedInV3Shell,
}) {
    useEffect(
        () => () => {
            const nextPath = history?.location?.pathname || "";
            const staysInMultipleTransfers = nextPath.startsWith("/transfer/multiple");
            const goesToSignOrCancel =
                nextPath.includes("/transactions/cancel") || nextPath.includes("/transactions/sign");
            if (!staysInMultipleTransfers && !goesToSignOrCancel) {
                dispatch(globalActions.resetSelectedElements());
            }
        },
        [history, dispatch],
    );
    const getListTrasfersMultiple = (page = 1, { force = false } = {}) => {
        if (!force && page === 1 && fetching) {
            return;
        }
        const defaultFilters = { ...filters, pageNumber: page };
        dispatch(multipleTransferActions.getMultipleTransfersList(defaultFilters, isDesktop));
    };

    useEffect(() => {
        getListTrasfersMultiple();
        // eslint-disable-next-line
    }, [dispatch, isDesktop, location?.key, location?.pathname, location?.hash]);

    const privateShellHomePath = getPostLoginHomePath(isUxDashboardImprovementsEnabled());

    const mobileShellRight = useMemo(
        () => (
            <ContextMenu
                color="white"
                imageStyle="pr-0"
                items={[
                    {
                        label: getMultipleTransfersI18nKey("multiple.transfers.create"),
                        onClick: () => {
                            dispatch(
                                notificationActions.showNotification(
                                    i18n.get("multiple.transfers.create.mobile.error"),
                                    "warning",
                                    ["multipleTransfer.list"],
                                ),
                            );
                        },
                        icon: "TransferAlt",
                    },
                ]}
            />
        ),
        [dispatch],
    );

    useEffect(() => {
        if (!embeddedInV3Shell) {
            return undefined;
        }
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["multipleTransfer.list"],
            titleKey: getMultipleTransfersI18nKey("multiple.transfers.title"),
            goBackPath: privateShellHomePath,
            rightContentMobile: !isDesktop ? mobileShellRight : undefined,
        });
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, mobileShellRight, privateShellHomePath]);

    const handleBack = () => dispatch(push(embeddedInV3Shell ? privateShellHomePath : "/desktop"));

    const handleTransfer = () => {
        dispatch(multipleTransferActions.setRememberFormData(false));
        dispatch(multipleTransferActions.multipleTransferPre());
    };

    const improvements = configUtils.getBoolean("frontend.show.MultipleTransfers.improvements.functionalities", false);

    const pageLoading = (fetching && isDesktop) || (fetching && !isDesktop && !pageNumber && !totalPages);

    const getCenterElement = () =>
        !isDesktop && (
            <I18n
                id={getMultipleTransfersI18nKey("multiple.transfers.title")}
                component="h1"
                componentProps={{ className: "m-0" }}
            />
        );

    return (
        <div className="transfer-multiple__dashboard">
            <div className={improvements ? "admin-detail-head px-5" : "admin-detail-head px-0"}>
                {!embeddedInV3Shell && (
                    <Notification scopeToShow="multipleTransfer.list" notificationClassname="navbar-fixed-top" />
                )}
                {(!embeddedInV3Shell || isDesktop) && (
                    <Head
                        onBack={handleBack}
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : undefined}
                        centerElement={() => getCenterElement()}
                        rightContent={() => {
                            if (!isDesktop) {
                                return mobileShellRight;
                            }
                            return undefined;
                        }}
                        accessibilityTextId="transfers.main.label"
                    />
                )}
                {isDesktop ? (
                    <div className="transfer-multiple__header-desktop">
                        <I18n id={getMultipleTransfersI18nKey("multiple.transfers.title")} component="h1" />
                        <Button
                            icon={improvements ? "Transfer" : undefined}
                            iconVariant="outline"
                            text={i18n.get(getMultipleTransfersI18nKey("multiple.transfers.create"))}
                            onClick={handleTransfer}
                        />
                    </div>
                ) : (
                    undefined
                )}
            </div>
            <div
                className={
                    improvements && isDesktop
                        ? "container transfer-multiple__data-container background-transparent px-5 pb-0"
                        : "container transfer-multiple__data-container background-transparent px-0 pb-0"
                }>
                <MultipleTransferFilters
                    isDesktop={isDesktop}
                    dispatch={dispatch}
                    isSmallDesktop={isSmallDesktop}
                    setValues={setValues}
                    filters={filters}
                    statusList={statusList}
                    fetching={fetching}
                />
            </div>
            <div className={improvements ? "" : "transfer-multiple__data-container"}>
                <PageLoading
                    className={classNames({ "screen-loader": !isDesktop, "line-loader my-5": isDesktop })}
                    loading={pageLoading}>
                    <MultipleTransferList
                        dispatch={dispatch}
                        isDesktop={isDesktop}
                        getListTrasfersMultiple={getListTrasfersMultiple}
                    />
                </PageLoading>
            </div>
        </div>
    );
}

MultipleTransferDashboard.propTypes = {
    isSmallDesktop: bool.isRequired,
    dispatch: func.isRequired,
    setValues: func.isRequired,
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
    filters: shape({}).isRequired,
    statusList: arrayOf(string).isRequired,
    pageNumber: number.isRequired,
    totalPages: number.isRequired,
    history: shape({}).isRequired,
    location: shape({
        pathname: string,
        key: string,
        hash: string,
    }),
    embeddedInV3Shell: bool,
};

MultipleTransferDashboard.defaultProps = {
    embeddedInV3Shell: false,
    location: {},
};

const mapStateToProps = (state) => ({
    fetching: multipleTransferSelectors.getFetching(state),
    pageNumber: multipleTransferSelectors.getPageNumber(state),
    totalPages: multipleTransferSelectors.getTotalPages(state),
    filters: multipleTransferSelectors.getFilters(state),
    statusList: multipleTransferSelectors.getStatusList(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(MultipleTransferDashboard);
